import {
  SHELL_AGENT_PARAM,
  applyAgentChatParam,
  replaceSharedUrl,
} from '@robinpowered/dashboard-apps-common';
import { applyProfileSidebarParams } from 'utils/profileSidebarParams';

/**
 * Canonical query param that opens the shell-hosted Robin Assistant sidebar.
 * Held in the URL (like the composer's `event` param and the profile
 * sidebar's `profile_user`) so a refresh restores the open panel.
 *
 * The param name and read helpers live in `common` (`agentChatParam.ts`
 * there) because MFEs derive the single right-hand sider policy from this
 * param too; only the shell writes it, so the write helpers stay here.
 */
export {
  SHELL_AGENT_PARAM,
  applyAgentChatParam,
  isAgentChatOpen,
} from '@robinpowered/dashboard-apps-common';
export { SHELL_AGENT_SIDER_ID } from '@robinpowered/dashboard-apps-common';

/**
 * Update the `agent` param while preserving the pathname and every other
 * param. Goes through `replaceSharedUrl` (not react-router's setSearchParams)
 * for the same reason the composer's `setEventParam` does: shell and each MFE
 * mount their own Router, and only the synthetic popstate from
 * `replaceSharedUrl` reaches all of them. Rebuilds from the live
 * `window.location` so MFE-committed params aren't silently dropped.
 */
const setAgentChatParam = (open: boolean): void => {
  const params = new URLSearchParams(window.location.search);
  if (open) {
    applyAgentChatParam(params);
    // Last-opened-wins across the shell's right-hand surfaces: opening the
    // assistant closes an open people profile (also URL-driven), so the two
    // never show together. The inverse lives in useProfileSidebarUrlState.
    applyProfileSidebarParams(params, { userId: null, view: 'compact' });
  } else {
    params.delete(SHELL_AGENT_PARAM);
  }
  const query = params.toString();
  replaceSharedUrl(`${window.location.pathname}${query ? `?${query}` : ''}`);
};

export const openAgentChat = (): void => setAgentChatParam(true);
export const closeAgentChat = (): void => setAgentChatParam(false);
